import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "../supabase";
import Sidebar from "../components/Sidebar";
import MobileHeader from "../components/MobileHeader";
import MobileManagerHeader from "../components/MobileManagerHeader";
import RepBottomNav from "../components/RepBottomNav";
import ManagerBottomNav from "../components/ManagerBottomNav";
import RepTopBar from "../components/RepTopBar";
import TopBar from "../components/TopBar";
import PullToRefresh from "../components/PullToRefresh";

export default function AccountDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const handleBack = () => {
    if (window.history.length > 1) { navigate(-1); } else { navigate("/leads"); }
  };

  const [profile, setProfile] = useState(null);
  const [account, setAccount] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [activities, setActivities] = useState([]);
  const [linkedApollo, setLinkedApollo] = useState([]);
  const [linkedEda, setLinkedEda] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCrmModal, setShowCrmModal] = useState(false);
  const [sendingToCRM, setSendingToCRM] = useState(false);
  const [crmSuccess, setCrmSuccess] = useState(false);

  // Notes
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesValue, setNotesValue] = useState("");
  const [notesSaved, setNotesSaved] = useState(false);

  // Edit company info
  const [editingCompany, setEditingCompany] = useState(false);
  const [companyForm, setCompanyForm] = useState({ name: "", address: "", contact_name: "", work_phone: "", mobile_phone: "", email: "", industry: "", source: "" });

  // Add / edit contact
  const [showAddContact, setShowAddContact] = useState(false);
  const [editingContactId, setEditingContactId] = useState(null);
  const [contactForm, setContactForm] = useState({
    first_name: "", last_name: "", title: "", phone: "", mobile_phone: "", email: "", branch_location: "", is_primary: false,
  });

  const STATUSES = ["New", "Contacted", "Engaged", "Proposal", "Won", "Lost"];

  const STATUS_COLORS = {
    New:       { bg: "#EFF6FF", color: "#2563EB", border: "#BFDBFE" },
    Contacted: { bg: "#FEFCE8", color: "#CA8A04", border: "#FDE68A" },
    Engaged:   { bg: "#F0FDF4", color: "#16A34A", border: "#BBF7D0" },
    Proposal:  { bg: "#FAF5FF", color: "#7C3AED", border: "#DDD6FE" },
    Won:       { bg: "#367C2B", color: "#FFFFFF", border: "#367C2B" },
    Lost:      { bg: "#FEF2F2", color: "#DC2626", border: "#FECACA" },
  };

  useEffect(() => { loadData(); }, [id]);

  const loadData = async () => {
    setLoading(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { navigate("/login"); return; }

    const { data: profileData } = await supabase
      .from("profiles").select("*").eq("id", user.id).single();
    setProfile(profileData);

    // Account
    const { data: acc } = await supabase
      .from("target_leads")
      .select("*, start_date, end_date")
      .eq("id", id)
      .single();
    setAccount(acc);
    setNotesValue(acc?.notes || "");
    setCompanyForm({
      name: acc?.name || "", address: acc?.address || "",
      contact_name: acc?.contact_name || "", work_phone: acc?.work_phone || "",
      mobile_phone: acc?.mobile_phone || "", email: acc?.email || "",
      industry: acc?.industry || "", source: acc?.source || "",
    });

    // Contacts
    const { data: cons } = await supabase
      .from("target_lead_contacts")
      .select("*")
      .eq("account_id", id)
      .order("is_primary", { ascending: false });
    setContacts(cons || []);

    // Activities
    const { data: acts } = await supabase
      .from("target_lead_activities")
      .select("*")
      .eq("account_id", id)
      .order("activity_date", { ascending: false });
    setActivities(acts || []);

    // Linked records
    const { data: apolloData } = await supabase
      .from("linked_apollo").select("*").eq("target_lead_id", id);
    setLinkedApollo(apolloData || []);

    const { data: edaData } = await supabase
      .from("linked_eda").select("*").eq("target_lead_id", id);
    setLinkedEda(edaData || []);

    setLoading(false);
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate("/login");
  };

  // ── Send to CRM ──
  const sendToCRM = async () => {
    if (profile?.role !== "manager") return;
    setSendingToCRM(true);

    try {
      const { data: { user: currentUser } } = await supabase.auth.getUser();

      console.log("sendToCRM - currentUser.id:", currentUser?.id);
      console.log("sendToCRM - profile.role:", profile?.role);

      const { data: { session } } = await supabase.auth.getSession();
      console.log("sendToCRM - session user id:", session?.user?.id);
      console.log("sendToCRM - session email:", session?.user?.email);

      const insertUserId = session?.user?.id;
      console.log("sendToCRM - insertUserId:", insertUserId);

      // Step 1 - Get fresh contacts and activities
      const { data: freshContacts } = await supabase
        .from("target_lead_contacts").select("*").eq("account_id", id);

      const { data: freshActivities } = await supabase
        .from("target_lead_activities").select("*").eq("account_id", id);

      // Step 2 - Insert into CRM accounts with rep assignment
      const { data: crmAccount, error: accountError } = await supabase
        .from("accounts")
        .insert({
          company_name: account?.name || account?.company || "Unknown",
          contact_name: account?.contact_name || null,
          address: account?.address || null,
          work_phone: account?.work_phone || null,
          mobile_phone: account?.mobile_phone || null,
          email: account?.email || null,
          industry: account?.industry || null,
          source: account?.source || null,
          notes: account?.notes || null,
          user_id: insertUserId,
        })
        .select()
        .single();

      if (accountError) throw accountError;

      // Step 2b - Update user_id to rep ID if account is assigned to a rep
      if (account?.rep_id) {
        const { error: assignError } = await supabase
          .from("accounts")
          .update({ user_id: account.rep_id })
          .eq("id", crmAccount.id);
        if (assignError) console.error("Rep assignment error:", assignError);
      }

      // Step 3 - Insert contacts into CRM contacts table
      if (freshContacts && freshContacts.length > 0) {
        for (const contact of freshContacts) {
          const { error: contactError } = await supabase
            .from("contacts")
            .insert({
              account_id: crmAccount.id,
              user_id: account?.rep_id || insertUserId,
              first_name: contact.first_name || "",
              last_name: contact.last_name || null,
              email: contact.email || null,
              phone: contact.phone || null,
              branch_location: contact.branch_location || null,
              is_primary: contact.is_primary || false,
            });
          if (contactError) console.error("Contact insert error:", contactError);
        }

        // Update contact_name on account from primary contact
        const primary = freshContacts.find(c => c.is_primary) || freshContacts[0];
        const contactName = `${primary.first_name || ""} ${primary.last_name || ""}`.trim();
        if (contactName) {
          await supabase
            .from("accounts")
            .update({ contact_name: contactName })
            .eq("id", crmAccount.id);
        }
      }

      // Step 4 - Insert activities into CRM activities table
      if (freshActivities && freshActivities.length > 0) {
        for (const activity of freshActivities) {
          const { error: activityError } = await supabase
            .from("activities")
            .insert({
              account_id: crmAccount.id,
              user_id: account?.rep_id || insertUserId,
              activity_type: activity.activity_type || null,
              outcome: activity.outcome || null,
              notes: activity.notes || null,
              activity_date: activity.activity_date || null,
              scheduled_next_type: activity.scheduled_next_type || null,
              scheduled_next_date: activity.scheduled_next_date || null,
              scheduled_next_time: activity.scheduled_next_time || null,
              scheduled_next_notes: activity.scheduled_next_notes || null,
            });
          if (activityError) console.error("Activity insert error:", activityError);
        }
      }

      // Step 5 - Transfer linked records to CRM account
      await supabase.from("linked_apollo")
        .update({ account_id: crmAccount.id, target_lead_id: null })
        .eq("target_lead_id", id);
      await supabase.from("linked_eda")
        .update({ account_id: crmAccount.id, target_lead_id: null })
        .eq("target_lead_id", id);

      // Step 6 - Delete from Target10
      await supabase.from("target_lead_activities").delete().eq("account_id", id);
      await supabase.from("target_lead_contacts").delete().eq("account_id", id);
      const { error: deleteError } = await supabase
        .from("target_leads").delete().eq("id", id);

      if (deleteError) throw deleteError;

      setShowCrmModal(false);
      setCrmSuccess(true);

      setTimeout(() => {
        if (profile?.role === "manager") {
          navigate("/dashboard/accounts");
        } else {
          navigate("/leads");
        }
      }, 1500);

    } catch (err) {
      console.error("Send to CRM error:", err);
      alert("Failed to send to CRM: " + err.message);
    } finally {
      setSendingToCRM(false);
    }
  };

  // ── Status update ──
  const updateStatus = async (status) => {
    await supabase.from("target_leads").update({ status }).eq("id", id);
    setAccount(prev => ({ ...prev, status }));
  };

  // ── Save notes ──
  const saveNotes = async () => {
    await supabase.from("target_leads").update({ notes: notesValue }).eq("id", id);
    setAccount(prev => ({ ...prev, notes: notesValue }));
    setEditingNotes(false);
    setNotesSaved(true);
    setTimeout(() => setNotesSaved(false), 2000);
  };

  // ── Save company info ──
  const saveCompany = async () => {
    const updates = {
      name: companyForm.name, address: companyForm.address,
      contact_name: companyForm.contact_name || null, work_phone: companyForm.work_phone || null,
      mobile_phone: companyForm.mobile_phone || null, email: companyForm.email || null,
      industry: companyForm.industry || null, source: companyForm.source || null,
    };
    await supabase.from("target_leads").update(updates).eq("id", id);
    setAccount(prev => ({ ...prev, ...updates }));
    setEditingCompany(false);
  };

  // ── Contact helpers ──
  const openAddContact = () => {
    setContactForm({ first_name: "", last_name: "", title: "", phone: "", email: "", is_primary: false });
    setEditingContactId(null);
    setShowAddContact(true);
  };

  const openEditContact = (contact) => {
    setContactForm({
      first_name: contact.first_name || "",
      last_name: contact.last_name || "",
      title: contact.title || "",
      phone: contact.phone || "",
      mobile_phone: contact.mobile_phone || "",
      email: contact.email || "",
      branch_location: contact.branch_location || "",
      is_primary: contact.is_primary || false,
    });
    setEditingContactId(contact.id);
    setShowAddContact(true);
  };

  const saveContact = async () => {
    if (editingContactId) {
      await supabase.from("target_lead_contacts").update(contactForm).eq("id", editingContactId);
    } else {
      await supabase.from("target_lead_contacts").insert({ ...contactForm, account_id: id });
    }
    setShowAddContact(false);
    setEditingContactId(null);
    loadData();
  };

  // ── Sprint progress ──
  const sprintProgress = () => {
    if (!account?.start_date || !account?.end_date) return 0;
    const start = new Date(account.start_date);
    const end = new Date(account.end_date);
    const today = new Date();
    return Math.min(100, Math.max(0, Math.round(((today - start) / (end - start)) * 100)));
  };

  const daysLeft = () => {
    if (!account?.end_date) return null;
    const diff = Math.ceil((new Date(account.end_date) - new Date()) / (1000 * 60 * 60 * 24));
    return diff > 0 ? diff : 0;
  };

  // ── Scheduled next activity ──
  const nextActivity = activities.find(a => a.scheduled_next_date);

  const safeDate = (d) => { const [y, m, day] = d.split("-"); return new Date(y, m - 1, day); };
  const formatDate = (d) => d ? safeDate(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "";
  const formatActivityDate = (d) => d ? safeDate(d).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "";

  if (loading) {
    return (
      <div style={styles.loadingPage}>
        <div style={styles.spinner} />
      </div>
    );
  }

  const statusStyle = STATUS_COLORS[account?.status] || STATUS_COLORS.New;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        body { background: #F5F5F3; font-family: 'DM Sans', sans-serif; }

        .green-link {
          background: none; border: none; padding: 0;
          color: #367C2B; font-size: 13px; font-weight: 600;
          font-family: 'DM Sans', sans-serif;
          cursor: pointer; text-decoration: underline;
        }
        .green-link:hover { color: #2d6b24; }

        .field-input {
          width: 100%; padding: 10px 12px;
          font-size: 14px; font-family: 'DM Sans', sans-serif;
          border: 1.5px solid #E0E0DC; border-radius: 6px;
          background: #fff; color: #1A1A1A; outline: none;
          transition: border-color 0.15s, box-shadow 0.15s;
        }
        .field-input:focus {
          border-color: #367C2B;
          box-shadow: 0 0 0 3px rgba(54,124,43,0.10);
        }

        .status-pill {
          padding: 6px 14px; border-radius: 100px;
          font-size: 12px; font-weight: 600;
          cursor: pointer; border: 2px solid transparent;
          transition: all 0.15s; font-family: 'DM Sans', sans-serif;
        }

        .btn-primary {
          background: #367C2B; color: #fff;
          border: none; border-radius: 6px;
          padding: 12px; font-size: 15px; font-weight: 600;
          font-family: 'DM Sans', sans-serif;
          cursor: pointer; transition: background 0.15s;
          width: 100%;
        }
        .btn-primary:hover { background: #2d6b24; }

        .btn-secondary {
          background: #fff; color: #767676;
          border: 1.5px solid #E0E0DC; border-radius: 6px;
          padding: 11px; font-size: 15px; font-weight: 500;
          font-family: 'DM Sans', sans-serif;
          cursor: pointer; transition: all 0.15s;
          width: 100%;
        }
        .btn-secondary:hover { border-color: #ABABAB; color: #1A1A1A; }

        @keyframes spin { to { transform: rotate(360deg); } }
        @media (max-width: 768px) {
          .main-content { margin-left: 0 !important; padding-top: 86px !important; }
          .detail-cards { gap: 8px !important; }
        }
      `}</style>

      <div style={styles.layout}>
        {profile?.role === "manager" ? (
          <>
            <MobileManagerHeader activePath="/dashboard/accounts" profile={profile} />
            <ManagerBottomNav activePath="/dashboard/accounts" />
            <Sidebar role="manager" profile={profile} onSignOut={handleSignOut} activePath="/dashboard/accounts" />
          </>
        ) : (
          <>
            <MobileHeader activePath="/leads" profile={profile} />
            <RepBottomNav activePath="/leads" />
            <Sidebar role="rep" profile={profile} onSignOut={handleSignOut} activePath="/leads" />
          </>
        )}
        <div className="main-content" style={styles.page}>
          {profile?.role === "manager"
            ? <TopBar profile={profile} onSignOut={handleSignOut} />
            : <RepTopBar profile={profile} onSignOut={handleSignOut} />
          }
          <PullToRefresh onRefresh={loadData}>

        {/* ── BACK LINK ── */}
        <button onClick={handleBack} style={{ background: "none", border: "none", padding: 0, fontSize: "14px", color: "#767676", cursor: "pointer", fontFamily: "'DM Sans', sans-serif", marginBottom: "16px" }}>
          ← {profile?.role === "manager" ? "Leads" : "My Leads"}
        </button>

        {/* ── HEADER ── */}
        <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "20px", flexWrap: "wrap" }}>
          <h1 style={{ fontSize: "24px", fontWeight: 600, color: "#1A1A1A", margin: 0 }}>{account?.name || account?.company}</h1>
          <span style={{
            ...styles.statusBadgeSmall,
            background: statusStyle.bg,
            color: statusStyle.color,
            border: `1px solid ${statusStyle.border}`,
          }}>
            {account?.status || "New"}
          </span>
          <button className="green-link" onClick={() => setEditingCompany(true)}>Edit</button>
          {profile?.role === "manager" && (
            <button
              onClick={() => setShowCrmModal(true)}
              style={{
                background: "#fff", border: "1.5px solid #367C2B", color: "#367C2B",
                borderRadius: "6px", padding: "6px 14px", fontSize: "13px", fontWeight: 600,
                cursor: "pointer", fontFamily: "'DM Sans', sans-serif", whiteSpace: "nowrap",
                marginLeft: "auto",
              }}
            >
              Send to CRM as Account
            </button>
          )}
        </div>

        <div className="detail-cards" style={styles.content}>

          {/* ── COMPANY INFO EDIT FORM ── */}
          {editingCompany && (
            <div style={styles.card}>
              <div style={styles.formStack}>
                <div style={styles.field}>
                  <label style={styles.fieldLabel}>Company Name</label>
                  <input className="field-input" value={companyForm.name}
                    onChange={e => setCompanyForm(p => ({ ...p, name: e.target.value }))} />
                </div>
                <div style={styles.field}>
                  <label style={styles.fieldLabel}>Contact Name</label>
                  <input className="field-input" value={companyForm.contact_name}
                    onChange={e => setCompanyForm(p => ({ ...p, contact_name: e.target.value }))} />
                </div>
                <div style={styles.field}>
                  <label style={styles.fieldLabel}>Address</label>
                  <input className="field-input" value={companyForm.address}
                    onChange={e => setCompanyForm(p => ({ ...p, address: e.target.value }))} />
                </div>
                <div style={styles.rowFields}>
                  <div style={styles.field}>
                    <label style={styles.fieldLabel}>Work Phone</label>
                    <input className="field-input" type="tel" value={companyForm.work_phone}
                      onChange={e => setCompanyForm(p => ({ ...p, work_phone: e.target.value }))} />
                  </div>
                  <div style={styles.field}>
                    <label style={styles.fieldLabel}>Mobile Phone</label>
                    <input className="field-input" type="tel" value={companyForm.mobile_phone}
                      onChange={e => setCompanyForm(p => ({ ...p, mobile_phone: e.target.value }))} />
                  </div>
                </div>
                <div style={styles.field}>
                  <label style={styles.fieldLabel}>Email</label>
                  <input className="field-input" type="email" value={companyForm.email}
                    onChange={e => setCompanyForm(p => ({ ...p, email: e.target.value }))} />
                </div>
                <div style={styles.rowFields}>
                  <div style={styles.field}>
                    <label style={styles.fieldLabel}>Industry</label>
                    <input className="field-input" value={companyForm.industry}
                      onChange={e => setCompanyForm(p => ({ ...p, industry: e.target.value }))} />
                  </div>
                  <div style={styles.field}>
                    <label style={styles.fieldLabel}>Source</label>
                    <input className="field-input" value={companyForm.source}
                      onChange={e => setCompanyForm(p => ({ ...p, source: e.target.value }))} />
                  </div>
                </div>
                <div style={styles.rowBtns}>
                  <button className="btn-primary" style={{ flex: 1 }} onClick={saveCompany}>Save</button>
                  <button className="btn-secondary" style={{ flex: 1 }} onClick={() => setEditingCompany(false)}>Cancel</button>
                </div>
              </div>
            </div>
          )}

          {/* ── INFO CARDS ── */}
          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
            {[
              { label: "Contact", value: account?.contact_name },
              { label: "Work Phone", value: account?.work_phone },
              { label: "Mobile Phone", value: account?.mobile_phone },
              { label: "Email", value: account?.email },
            ].map(card => (
              <div key={card.label} style={{ ...styles.card, flex: 1, minWidth: "120px", gap: "4px" }}>
                <p style={{ fontSize: "12px", color: "#767676", fontWeight: 500 }}>{card.label}</p>
                <p style={{ fontSize: "14px", color: "#1A1A1A", fontWeight: 500 }}>{card.value || "—"}</p>
              </div>
            ))}
          </div>

          {/* ── NOTES ── */}
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <p style={styles.cardLabel}>Notes</p>
              {!editingNotes && (
                <button className="green-link" onClick={() => setEditingNotes(true)}>Add Note</button>
              )}
            </div>
            {editingNotes ? (
              <div style={styles.formStack}>
                <textarea
                  className="field-input"
                  rows={4}
                  style={{ resize: "vertical" }}
                  value={notesValue}
                  onChange={e => setNotesValue(e.target.value)}
                  placeholder="Add notes about this lead..."
                />
                <div style={styles.rowBtns}>
                  <button className="btn-primary" style={{ flex: 1 }} onClick={saveNotes}>Save</button>
                  <button className="btn-secondary" style={{ flex: 1 }} onClick={() => { setEditingNotes(false); setNotesValue(account?.notes || ""); }}>Cancel</button>
                </div>
              </div>
            ) : (
              <div>
                {account?.notes
                  ? <div style={{ fontSize: "14px", color: "#374151", whiteSpace: "pre-wrap", lineHeight: 1.6 }}>
                      {account.notes.split(/(https?:\/\/[^\s<]+|<a\s[^>]*>.*?<\/a>)/gi).map((part, i) => {
                        if (part.match(/^<a\s/i)) {
                          const href = part.match(/href="([^"]+)"/)?.[1] || "#";
                          const text = part.match(/>([^<]+)</)?.[1] || "Link";
                          return <a key={i} href={href} target="_blank" rel="noopener noreferrer" style={{ color: "#367C2B", textDecoration: "underline" }}>{text}</a>;
                        }
                        if (part.match(/^https?:\/\//)) {
                          let label = "Link";
                          try { label = new URL(part).hostname.replace("www.", ""); } catch {}
                          return <a key={i} href={part} target="_blank" rel="noopener noreferrer" style={{ color: "#367C2B", textDecoration: "underline" }}>{label}</a>;
                        }
                        return part;
                      })}
                    </div>
                  : <p style={styles.infoMuted}>Add notes about this lead...</p>
                }
                {notesSaved && <p style={{ fontSize: "12px", color: "#16A34A", marginTop: "6px" }}>Notes saved</p>}
              </div>
            )}
          </div>

          {/* ── CONTACTS ── */}
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <p style={styles.cardLabel}>Contacts</p>
              <button className="green-link" onClick={openAddContact}>+ Add contact</button>
            </div>

            {/* Add / Edit contact form */}
            {showAddContact && (
              <div style={styles.formStack}>
                <div style={styles.field}>
                  <label style={styles.fieldLabel}>Contact</label>
                  <input className="field-input" value={contactForm.first_name}
                    onChange={e => setContactForm(p => ({ ...p, first_name: e.target.value }))}
                    placeholder="Full name" />
                </div>
                <div style={styles.rowFields}>
                  <div style={styles.field}>
                    <label style={styles.fieldLabel}>Work Phone</label>
                    <input className="field-input" type="tel" value={contactForm.phone}
                      onChange={e => setContactForm(p => ({ ...p, phone: e.target.value }))} />
                  </div>
                  <div style={styles.field}>
                    <label style={styles.fieldLabel}>Mobile Phone</label>
                    <input className="field-input" type="tel" value={contactForm.mobile_phone || ""}
                      onChange={e => setContactForm(p => ({ ...p, mobile_phone: e.target.value }))} />
                  </div>
                </div>
                <div style={styles.field}>
                  <label style={styles.fieldLabel}>Email</label>
                  <input className="field-input" type="email" value={contactForm.email}
                    onChange={e => setContactForm(p => ({ ...p, email: e.target.value }))} />
                </div>
                <div style={styles.rowBtns}>
                  <button className="btn-primary" style={{ flex: 1 }} onClick={saveContact}>
                    {editingContactId ? "Save changes" : "Add contact"}
                  </button>
                  <button className="btn-secondary" style={{ flex: 1 }} onClick={() => { setShowAddContact(false); setEditingContactId(null); }}>
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Contact table */}
            {(() => {
              const cTh = { backgroundColor: "#ffffff", fontSize: "11px", fontWeight: 600, color: "#ABABAB", padding: "10px 16px", textAlign: "left", borderBottom: "1px solid #E8E8E6", textTransform: "uppercase", letterSpacing: "0.06em" };
              const cTd = { fontSize: "13px", color: "#374151", padding: "12px 16px", borderBottom: "1px solid #F0F0ED" };
              const hasLeadContact = account?.contact_name || account?.work_phone || account?.mobile_phone || account?.email;
              const leadContactAlreadyInTable = contacts.some(c =>
                `${c.first_name || ""} ${c.last_name || ""}`.trim().toLowerCase() === (account?.contact_name || "").toLowerCase()
              );
              const showLeadRow = hasLeadContact && !leadContactAlreadyInTable && contacts.length === 0;
              return contacts.length === 0 && !showLeadRow && !showAddContact ? (
                <p style={styles.infoMuted}>No contacts added yet</p>
              ) : (
                <div style={{ border: "0.5px solid #E8E8E6", borderRadius: "6px", overflow: "auto", WebkitOverflowScrolling: "touch" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr>
                        {["Contact", "Work Phone", "Mobile Phone", "Email", ""].map(h => (
                          <th key={h} style={cTh}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {showLeadRow && (
                        <tr>
                          <td style={{ ...cTd, fontWeight: 500, color: "#1A1A1A" }}>{account.contact_name || "—"}</td>
                          <td style={cTd}>{account.work_phone || "—"}</td>
                          <td style={cTd}>{account.mobile_phone || "—"}</td>
                          <td style={cTd}>{account.email || "—"}</td>
                          <td style={cTd} />
                        </tr>
                      )}
                      {contacts.map(contact => (
                        <tr key={contact.id}>
                          <td style={{ ...cTd, fontWeight: 500, color: "#1A1A1A" }}>
                            {`${contact.first_name || ""} ${contact.last_name || ""}`.trim() || "—"}
                            {contact.is_primary && <span style={styles.primaryBadge}> Primary</span>}
                          </td>
                          <td style={cTd}>{contact.phone || contact.work_phone || "—"}</td>
                          <td style={cTd}>{contact.mobile_phone || "—"}</td>
                          <td style={cTd}>{contact.email || "—"}</td>
                          <td style={{ ...cTd, padding: "12px 16px" }}>
                            <button className="green-link" onClick={() => openEditContact(contact)}>Edit</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })()}
          </div>

          {/* ── LINKED APOLLO RECORDS ── */}
          {linkedApollo.length > 0 && (
            <>
              <p style={{ fontSize: "16px", fontWeight: 600, color: "#1A1A1A", margin: "8px 0 4px" }}>Linked Apollo Records</p>
              <div style={{ ...styles.card, padding: 0, overflow: "auto", WebkitOverflowScrolling: "touch" }}>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr>
                        {["Company", "Website", "Industry", "City/State", "Phone", "Employees"].map(h => (
                          <th key={h} style={{ backgroundColor: "#ffffff", fontSize: "11px", fontWeight: 600, color: "#ABABAB", padding: "10px 16px", textAlign: "left", borderBottom: "1px solid #E8E8E6", textTransform: "uppercase", letterSpacing: "0.06em" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {linkedApollo.map(a => (
                        <tr key={a.id}>
                          <td style={{ fontSize: "13px", color: "#1A1A1A", fontWeight: 500, padding: "12px 16px", borderBottom: "1px solid #F0F0ED" }}>{a.company_name || "—"}</td>
                          <td style={{ fontSize: "13px", color: "#374151", padding: "12px 16px", borderBottom: "1px solid #F0F0ED" }}>
                            {a.website ? <a href={a.website.startsWith("http") ? a.website : `https://${a.website}`} target="_blank" rel="noopener noreferrer" style={{ color: "#367C2B", textDecoration: "underline" }}>{a.website}</a> : "—"}
                          </td>
                          <td style={{ fontSize: "13px", color: "#374151", padding: "12px 16px", borderBottom: "1px solid #F0F0ED" }}>{a.industry || "—"}</td>
                          <td style={{ fontSize: "13px", color: "#374151", padding: "12px 16px", borderBottom: "1px solid #F0F0ED" }}>{[a.city, a.state].filter(Boolean).join(", ") || "—"}</td>
                          <td style={{ fontSize: "13px", color: "#374151", padding: "12px 16px", borderBottom: "1px solid #F0F0ED" }}>{a.phone || "—"}</td>
                          <td style={{ fontSize: "13px", color: "#374151", padding: "12px 16px", borderBottom: "1px solid #F0F0ED" }}>{a.employee_count || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {/* ── LINKED EDA RECORDS ── */}
          {linkedEda.length > 0 && (
            <>
              <p style={{ fontSize: "16px", fontWeight: 600, color: "#1A1A1A", margin: "8px 0 4px" }}>Linked EDA Records</p>
              <div style={{ ...styles.card, padding: 0, overflow: "auto", WebkitOverflowScrolling: "touch" }}>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr>
                        {["Company", "County", "State", "Buying Probability", "Fleet Size", "Equipment", "EDA Link"].map(h => (
                          <th key={h} style={{ backgroundColor: "#ffffff", fontSize: "11px", fontWeight: 600, color: "#ABABAB", padding: "10px 16px", textAlign: "left", borderBottom: "1px solid #E8E8E6", textTransform: "uppercase", letterSpacing: "0.06em" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {linkedEda.map(e => (
                        <tr key={e.id}>
                          <td style={{ fontSize: "13px", color: "#1A1A1A", fontWeight: 500, padding: "12px 16px", borderBottom: "1px solid #F0F0ED" }}>{e.buycomp1 || "—"}</td>
                          <td style={{ fontSize: "13px", color: "#374151", padding: "12px 16px", borderBottom: "1px solid #F0F0ED" }}>{e.buycty || "—"}</td>
                          <td style={{ fontSize: "13px", color: "#374151", padding: "12px 16px", borderBottom: "1px solid #F0F0ED" }}>{e.buystate || "—"}</td>
                          <td style={{ fontSize: "13px", color: "#374151", padding: "12px 16px", borderBottom: "1px solid #F0F0ED" }}>{e.buyingprobability || "—"}</td>
                          <td style={{ fontSize: "13px", color: "#374151", padding: "12px 16px", borderBottom: "1px solid #F0F0ED" }}>{e.fleetcount || "—"}</td>
                          <td style={{ fontSize: "13px", color: "#374151", padding: "12px 16px", borderBottom: "1px solid #F0F0ED" }}>{e.eqtdesc || "—"}</td>
                          <td style={{ fontSize: "13px", padding: "12px 16px", borderBottom: "1px solid #F0F0ED" }}>
                            {e.prospectprofilelink ? <a href={e.prospectprofilelink.startsWith("http") ? e.prospectprofilelink : `https://${e.prospectprofilelink}`} target="_blank" rel="noopener noreferrer" style={{ color: "#367C2B", textDecoration: "underline", fontWeight: 600 }}>View</a> : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {/* ── STATUS SELECTOR ── */}
          <div style={styles.card}>
            <p style={styles.cardLabel}>Lead Status</p>
            <div style={styles.statusRow}>
              {STATUSES.map(s => {
                const sc = STATUS_COLORS[s];
                const isActive = account?.status === s;
                return (
                  <button
                    key={s}
                    className="status-pill"
                    onClick={() => updateStatus(s)}
                    style={{
                      background: sc.bg,
                      color: sc.color,
                      borderColor: isActive ? sc.color : "transparent",
                      outline: isActive ? `2px solid ${sc.color}` : "none",
                      outlineOffset: "2px",
                    }}
                  >
                    {s}
                  </button>
                );
              })}
            </div>
          </div>

          {/* ── SCHEDULED NEXT ACTIVITY ── */}
          {nextActivity && (
            <div style={styles.scheduledCard}>
              <div style={styles.cardHeader}>
                <p style={styles.cardLabel}>Scheduled Next Activity</p>
              </div>
              <p style={styles.scheduledType}>{nextActivity.scheduled_next_type}</p>
              <p style={styles.scheduledDate}>
                {formatDate(nextActivity.scheduled_next_date)}
                {nextActivity.scheduled_next_time ? ` · ${nextActivity.scheduled_next_time}` : ""}
              </p>
              {nextActivity.scheduled_next_notes && (
                <p style={styles.scheduledNotes}>{nextActivity.scheduled_next_notes}</p>
              )}
              <p style={styles.outlookNote}>✓ Added to Outlook calendar</p>
            </div>
          )}

          {/* ── SPRINT PROGRESS ── */}
          {account?.end_date && (
            <div style={styles.card}>
              <div style={styles.cardHeader}>
                <p style={styles.cardLabel}>Target Progress</p>
                <span style={styles.daysLeftText}>{daysLeft()} days left</span>
              </div>
              <div style={styles.progressTrack}>
                <div style={{ ...styles.progressFill, width: `${sprintProgress()}%` }} />
              </div>
            </div>
          )}

          {/* ── ACTIVITIES ── */}
          {(() => {
            const d = new Date();
            const todayStr = d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0");
            const upcoming = activities.filter(a => a.activity_date && a.activity_date > todayStr).sort((a, b) => a.activity_date.localeCompare(b.activity_date));
            const past = activities.filter(a => !a.activity_date || a.activity_date <= todayStr);
            const actTh = { backgroundColor: "#ffffff", fontSize: "11px", fontWeight: 600, color: "#ABABAB", position: "sticky", top: 0, zIndex: 1, padding: "10px 16px", textAlign: "left", borderBottom: "1px solid #E8E8E6", textTransform: "uppercase", letterSpacing: "0.06em" };
            const actTd = { fontSize: "13px", color: "#374151", padding: "12px 16px", borderBottom: "1px solid #F0F0ED" };
            return (
              <>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", margin: "8px 0 4px" }}>
                  <p style={{ fontSize: "16px", fontWeight: 600, color: "#1A1A1A" }}>Activities</p>
                  {profile?.role === "rep" && (
                    <button
                      onClick={() => navigate(`/leads/${id}/log`)}
                      style={{ backgroundColor: "#367C2B", color: "#fff", padding: "6px 12px", borderRadius: "6px", fontSize: "13px", fontWeight: 600, border: "none", cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}
                    >
                      + Add Activity
                    </button>
                  )}
                </div>

                <p style={{ fontSize: "14px", fontWeight: 600, color: "#1A1A1A", margin: "12px 0 8px" }}>Upcoming</p>
                <div style={{ ...styles.card, padding: 0, overflow: "auto", WebkitOverflowScrolling: "touch" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr>
                        {["Date", "Type", "Outcome", "Notes"].map(h => (
                          <th key={h} style={actTh}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {upcoming.length === 0 ? (
                        <tr><td colSpan={4} style={{ ...actTd, textAlign: "center", color: "#ABABAB" }}>No upcoming activities</td></tr>
                      ) : upcoming.map(act => (
                        <tr key={act.id}>
                          <td style={actTd}>{formatActivityDate(act.activity_date)}</td>
                          <td style={actTd}>{act.activity_type || "—"}</td>
                          <td style={actTd}>{act.outcome || "—"}</td>
                          <td style={actTd}>{act.notes || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <p style={{ fontSize: "14px", fontWeight: 600, color: "#1A1A1A", margin: "16px 0 8px" }}>Past</p>
                <div style={{ ...styles.card, padding: 0, overflow: "auto", WebkitOverflowScrolling: "touch" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr>
                        {["Date", "Type", "Outcome", "Notes"].map(h => (
                          <th key={h} style={actTh}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {past.length === 0 ? (
                        <tr><td colSpan={4} style={{ ...actTd, textAlign: "center", color: "#ABABAB" }}>No past activities</td></tr>
                      ) : past.map(act => (
                        <tr key={act.id}>
                          <td style={actTd}>{formatActivityDate(act.activity_date)}</td>
                          <td style={actTd}>{act.activity_type || "—"}</td>
                          <td style={actTd}>{act.outcome || "—"}</td>
                          <td style={actTd}>{act.notes || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            );
          })()}

          <div style={{ marginBottom: "95px" }} />

        </div>
                  </PullToRefresh>
        </div>
      </div>

      {/* ── CRM Confirmation Modal ── */}
      {showCrmModal && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: "#fff", borderRadius: "8px", padding: "24px", maxWidth: "420px", width: "90%", fontFamily: "'DM Sans', sans-serif" }}>
            <div style={{ fontSize: "16px", fontWeight: 600, marginBottom: "8px", color: "#1A1A1A" }}>
              Send to CRM as Account?
            </div>
            <div style={{ fontSize: "13px", color: "#767676", marginBottom: "20px", lineHeight: 1.5 }}>
              This lead and all its activity will be moved to the CRM accounts and removed from Target10.
            </div>
            <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
              <button
                onClick={() => setShowCrmModal(false)}
                style={{ background: "#fff", border: "1.5px solid #E0E0DC", color: "#767676", borderRadius: "6px", padding: "8px 16px", fontSize: "13px", cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}
              >
                Cancel
              </button>
              <button
                onClick={sendToCRM}
                disabled={sendingToCRM}
                style={{ background: "#367C2B", border: "none", color: "#fff", borderRadius: "6px", padding: "8px 16px", fontSize: "13px", fontWeight: 600, cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}
              >
                {sendingToCRM ? "Sending..." : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── CRM Success Toast ── */}
      {crmSuccess && (
        <div style={{ position: "fixed", bottom: "24px", right: "24px", background: "#367C2B", color: "#fff", padding: "12px 20px", borderRadius: "6px", fontSize: "13px", fontWeight: 600, zIndex: 1000, fontFamily: "'DM Sans', sans-serif" }}>
          Successfully sent to CRM!
        </div>
      )}
    </>
  );
}

const styles = {
  layout: {
    display: "flex",
    minHeight: "100vh",
    backgroundColor: "#F5F5F3",
  },
  page: {
    marginLeft: "220px",
    flex: 1,
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    backgroundColor: "#F5F5F3",
    fontFamily: "'DM Sans', sans-serif",
    padding: "28px 32px 40px",
  },
  loadingPage: {
    minHeight: "100vh", display: "flex",
    alignItems: "center", justifyContent: "center",
    backgroundColor: "#F5F5F3",
  },
  spinner: {
    width: "32px", height: "32px", borderRadius: "50%",
    border: "3px solid #E0E0DC", borderTopColor: "#367C2B",
    animation: "spin 0.8s linear infinite",
  },

  // Header
  header: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "14px 20px",
    backgroundColor: "#ffffff",
    borderBottom: "1px solid #E8E8E6",
    position: "sticky", top: 0, zIndex: 10,
  },
  backBtn: {
    background: "none", border: "none", padding: 0,
    fontSize: "14px", color: "#367C2B", fontWeight: 600,
    cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
  },
  headerLogo: { height: "28px", objectFit: "contain" },
  statusBadgeSmall: {
    fontSize: "11px", fontWeight: 600,
    padding: "4px 10px", borderRadius: "100px",
  },

  pageTitleWrap: {
    padding: "16px 20px 8px",
    backgroundColor: "#ffffff",
    borderBottom: "1px solid #E8E8E6",
  },
  pageTitle: {
    fontSize: "20px", fontWeight: 600, color: "#1A1A1A",
  },

  content: {
    padding: "16px 20px",
    display: "flex", flexDirection: "column", gap: "12px",
  },

  // Cards
  card: {
    backgroundColor: "#ffffff",
    border: "1px solid #E8E8E6",
    borderRadius: "8px",
    padding: "16px",
    display: "flex", flexDirection: "column", gap: "12px",
  },
  cardHeader: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
  },
  cardLabel: {
    fontSize: "16px", fontWeight: 600, color: "#1A1A1A",
  },

  // Company info
  infoStack: { display: "flex", flexDirection: "column", gap: "4px" },
  infoValue: { fontSize: "15px", fontWeight: 500, color: "#1A1A1A" },
  infoMuted: { fontSize: "13px", color: "#ABABAB" },
  addressLink: { fontSize: "13px", color: "#367C2B", fontWeight: 500 },

  // Form
  formStack: { display: "flex", flexDirection: "column", gap: "12px" },
  rowFields: { display: "flex", gap: "10px" },
  field: { display: "flex", flexDirection: "column", gap: "5px", flex: 1 },
  fieldLabel: { fontSize: "12px", fontWeight: 600, color: "#374151" },
  rowBtns: { display: "flex", gap: "10px" },
  checkboxRow: {
    display: "flex", alignItems: "center", gap: "8px", cursor: "pointer",
  },

  // Contacts
  contactCard: {
    borderTop: "1px solid #F0F0ED",
    paddingTop: "12px",
    display: "flex", flexDirection: "column", gap: "8px",
  },
  contactTop: { display: "flex", justifyContent: "space-between", alignItems: "flex-start" },
  contactName: { fontSize: "14px", fontWeight: 600, color: "#1A1A1A", display: "flex", alignItems: "center", gap: "8px" },
  contactTitle: { fontSize: "12px", color: "#767676", marginTop: "2px" },
  primaryBadge: {
    fontSize: "10px", fontWeight: 600, color: "#367C2B",
    background: "#F0FDF4", border: "1px solid #BBF7D0",
    borderRadius: "100px", padding: "2px 8px",
  },
  contactLinks: { display: "flex", flexDirection: "column", gap: "4px" },
  contactLink: { fontSize: "13px", color: "#367C2B", fontWeight: 500, textDecoration: "none" },

  // Status
  statusRow: { display: "flex", flexWrap: "wrap", gap: "8px" },

  // Scheduled
  scheduledCard: {
    backgroundColor: "#FFFBEB",
    border: "1px solid #FDE68A",
    borderRadius: "8px",
    padding: "16px",
    display: "flex", flexDirection: "column", gap: "6px",
  },
  scheduledType: { fontSize: "14px", fontWeight: 600, color: "#1A1A1A" },
  scheduledDate: { fontSize: "13px", color: "#767676" },
  scheduledNotes: { fontSize: "13px", color: "#374151" },
  outlookNote: { fontSize: "12px", color: "#16A34A", marginTop: "4px" },

  // Sprint progress
  daysLeftText: { fontSize: "12px", color: "#767676" },
  progressTrack: {
    height: "6px", backgroundColor: "#F0F0ED",
    borderRadius: "3px", overflow: "hidden",
  },
  progressFill: {
    height: "100%", backgroundColor: "#367C2B",
    borderRadius: "3px", transition: "width 0.3s ease",
  },

  // Timeline
  timeline: { display: "flex", flexDirection: "column", gap: "0" },
  timelineItem: { display: "flex", gap: "12px", paddingBottom: "16px" },
  timelineDotWrap: { display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 },
  timelineDot: { width: "10px", height: "10px", borderRadius: "50%", backgroundColor: "#367C2B", marginTop: "4px" },
  timelineLine: { flex: 1, width: "1px", backgroundColor: "#E8E8E6", marginTop: "4px" },
  timelineContent: { flex: 1, paddingBottom: "4px" },
  timelineHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" },
  activityType: { fontSize: "13px", fontWeight: 600, color: "#1A1A1A" },
  activityDate: { fontSize: "12px", color: "#ABABAB" },
  activityOutcome: { fontSize: "13px", color: "#374151", marginBottom: "2px" },
  activityNotes: { fontSize: "13px", color: "#767676" },

};
